// Moving a job along its workflow (docs/job-view-brief.md, section 1): one way to mark a step done, from the Details
// tab's "move here", the Engineering check's finish, or the Engineering lock. The database refuses a move past an
// Engineering gate while anything on the job is unverified (ug.job_past_engineering); the pipeline hears the events a
// step raises (the Proposal step done; every job's Engineering check done).

import { STEP } from '@zerogap/engine';
import { toast, updateRow, upsertRow, type Row } from './api';
import { today } from './format';
import { tickByEvent } from './pipeline';

/** The job moves to this step: its stage, the step's checklist row dated today, and the pipeline told of the events it raises. */
export async function moveJobToStep(job: Row, steps: Row[], stepId: string): Promise<boolean> {
  const step = steps.find((x) => x.id === stepId);
  if (!step) return false;
  if (step.lock_point && !window.confirm(`"${step.name}" is a lock point. Move this job there?`)) return false;
  try {
    await updateRow('job', job.id, { stage_id: stepId });
  } catch {
    // the database said why (the Engineering gate); the write helper toasted it
    return false;
  }
  await upsertRow('job_step', { job_id: job.id, workflow_step_id: stepId, actual_date: today() }, 'job_id,workflow_step_id');
  await raiseEvents(job, steps, step);
  return true;
}

/** Mark one step done without moving the job past it (the walk finishing while the job stays on its step): the checklist row dated, and the events told. */
export async function markStepDone(job: Row, steps: Row[], stepKey: string): Promise<void> {
  const step = steps.find((x) => x.key === stepKey);
  if (!step) return;
  await upsertRow('job_step', { job_id: job.id, workflow_step_id: step.id, actual_date: today() }, 'job_id,workflow_step_id');
  const current = steps.find((x) => x.id === job.stage_id);
  // the job's own position catches up to a step it has finished
  if (!current || Number(current.sort) < Number(step.sort)) {
    try {
      await updateRow('job', job.id, { stage_id: step.id });
    } catch {
      /* the gate said why */
    }
  }
  await raiseEvents(job, steps, step);
}

async function raiseEvents(job: Row, steps: Row[], step: Row) {
  if (!job.opportunity_id) return;
  const proposal = steps.find((x) => x.key === STEP.proposal);
  if (proposal && Number(step.sort) >= Number(proposal.sort) && (await tickByEvent(job.opportunity_id, 'job_proposal_done'))) toast("The lead's proposal step is done; it moved on.");
  const check = steps.find((x) => x.key === 'engineering_check');
  if (check && Number(step.sort) >= Number(check.sort) && (await tickByEvent(job.opportunity_id, 'job_engineering_check_done'))) toast("The lead's Engineering check step is done; it moved on.");
}
